import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, X, Users, FileText, Briefcase, Ruler, Receipt, Clock } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isRouteAllowedFor } from "@/lib/roles";

const RECENTS_KEY = "factlify_recents";
const MAX_RECENTS = 5;

const QUICK_ACTIONS = [
  { label: "Nuevo presupuesto", path: "/quotes/new", icon: FileText },
  { label: "Nueva medición", path: "/measurements/new", icon: Ruler },
  { label: "Ver clientes", path: "/clients", icon: Users },
  { label: "Ver obras", path: "/projects", icon: Briefcase },
];

const TYPE_ICON = { client: Users, quote: FileText, project: Briefcase, invoice: Receipt };
const TYPE_LABEL = { client: "Clientes", quote: "Presupuestos", project: "Obras", invoice: "Facturas" };

const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

function loadRecents() {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); }
  catch { return []; }
}

function saveRecent(item) {
  try {
    const prev = loadRecents().filter((r) => !(r.type === item.type && r.id === item.id));
    localStorage.setItem(RECENTS_KEY, JSON.stringify([item, ...prev].slice(0, MAX_RECENTS)));
  } catch {}
}

export default function CommandPalette() {
  const { user } = useAuth();
  const { role } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ clients: [], quotes: [], projects: [], invoices: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") close();
    };
    const customHandler = () => setOpen(true);
    window.addEventListener("keydown", handler);
    window.addEventListener("factlify:cmdpalette", customHandler);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("factlify:cmdpalette", customHandler);
    };
  }, [close]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setResults({ clients: [], quotes: [], projects: [], invoices: [] });
      setActiveIdx(0);
      setRecents(loadRecents());
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults({ clients: [], quotes: [], projects: [], invoices: [] });
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/search", { params: { q: query, limit: 5 } });
        setResults({
          clients: r.data.clients || [],
          quotes: r.data.quotes || [],
          projects: r.data.projects || [],
          invoices: r.data.invoices || [],
        });
      } catch (_) {}
      finally { setLoading(false); }
    }, 240);
  }, [query]);

  const visibleResults = {
    clients: isRouteAllowedFor(role, "/clients") ? results.clients : [],
    quotes: isRouteAllowedFor(role, "/quotes") ? results.quotes : [],
    invoices: isRouteAllowedFor(role, "/invoices") ? results.invoices : [],
    projects: isRouteAllowedFor(role, "/projects") ? results.projects : [],
  };
  const flat = [
    ...visibleResults.clients.map((c) => ({ type: "client", id: c.id, label: c.name, sub: c.phone || c.email || "", path: `/clients/${c.id}` })),
    ...visibleResults.quotes.map((q) => ({ type: "quote", id: q.id, label: q.title, sub: `${q.number} · ${q.client_name || ""}`, path: `/quotes/${q.id}`, amount: q.amount })),
    ...visibleResults.invoices.map((inv) => ({ type: "invoice", id: inv.id, label: inv.number, sub: inv.client_name || "", path: "/invoices", amount: inv.total })),
    ...visibleResults.projects.map((p) => ({ type: "project", id: p.id, label: p.title, sub: p.client_name || "", path: "/projects" })),
  ];
  const quickActions = QUICK_ACTIONS.filter((item) => isRouteAllowedFor(role, item.path));
  const visibleRecents = recents.filter((item) => isRouteAllowedFor(role, item.path));

  const go = useCallback((item) => {
    saveRecent(item);
    navigate(item.path);
    close();
  }, [navigate, close]);

  const goAction = useCallback((path) => { navigate(path); close(); }, [navigate, close]);

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && flat[activeIdx]) go(flat[activeIdx]);
  };

  if (!user || !open) return null;

  const renderGroup = (items, type, offset) =>
    items.length === 0 ? null : (
      <div key={type}>
        <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-steel bg-slate-50 border-y border-slate-200">
          {TYPE_LABEL[type]}
        </div>
        {items.map((item, i) => {
          const idx = offset + i;
          const Icon = TYPE_ICON[type];
          return (
            <button
              key={item.id}
              type="button"
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => go(flat[idx])}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${activeIdx === idx ? "bg-blueprint/8 border-l-2 border-blueprint" : "hover:bg-slate-50 border-l-2 border-transparent"}`}
            >
              <Icon size={16} weight="bold" className="text-steel shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{item.label}</div>
                {item.sub && <div className="text-xs text-steel truncate">{item.sub}</div>}
              </div>
              {item.amount != null && (
                <div className="font-mono text-sm font-bold text-blueprint shrink-0">{fmt(item.amount)}</div>
              )}
            </button>
          );
        })}
      </div>
    );

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[8vh] bg-ink/60 backdrop-blur-sm"
      onClick={close}
      role="presentation"
    >
      <div
        className="w-full max-w-lg mx-4 bg-white border-2 border-ink shadow-[8px_8px_0px_#09090B]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Búsqueda global"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 border-b-2 border-ink">
          <MagnifyingGlass size={18} weight="bold" aria-hidden="true" className="text-steel shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKey}
            aria-label="Buscar clientes, presupuestos, facturas, obras"
            placeholder="Buscar clientes, presupuestos, facturas, obras…"
            className="flex-1 py-4 text-base outline-none bg-transparent"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Limpiar búsqueda" className="text-steel hover:text-ink">
              <X size={16} weight="bold" aria-hidden="true" />
            </button>
          )}
          <button type="button" onClick={close} className="text-steel hover:text-ink ml-1">
            <kbd className="text-[10px] font-mono border border-slate-300 px-1.5 py-0.5 rounded">Esc</kbd>
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto">
          {query.trim() ? (
            loading ? (
              <div className="py-8 text-center text-steel text-sm font-mono">Buscando…</div>
            ) : flat.length === 0 ? (
              <div className="py-8 text-center text-steel text-sm">Sin resultados para <b>"{query}"</b></div>
            ) : (
              <>
                {renderGroup(visibleResults.clients, "client", 0)}
                {renderGroup(visibleResults.quotes, "quote", visibleResults.clients.length)}
                {renderGroup(visibleResults.invoices, "invoice", visibleResults.clients.length + visibleResults.quotes.length)}
                {renderGroup(visibleResults.projects, "project", visibleResults.clients.length + visibleResults.quotes.length + visibleResults.invoices.length)}
              </>
            )
          ) : (
            <div className="p-3">
              {visibleRecents.length > 0 && (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-steel mb-2 px-1 flex items-center gap-1.5">
                    <Clock size={11} weight="bold" /> Recientes
                  </div>
                  {visibleRecents.map((r) => {
                    const Icon = TYPE_ICON[r.type] || FileText;
                    return (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        onClick={() => { navigate(r.path); close(); }}
                        className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-slate-50 flex items-center gap-3 border border-transparent hover:border-slate-200 transition-colors"
                      >
                        <Icon size={14} weight="bold" className="text-steel shrink-0" />
                        <span className="text-sm font-medium truncate">{r.label}</span>
                        {r.sub && <span className="text-xs text-steel truncate ml-1">{r.sub}</span>}
                        <span className="ml-auto text-steel text-[10px] opacity-60 shrink-0">{TYPE_LABEL[r.type]}</span>
                      </button>
                    );
                  })}
                  <div className="my-2 border-t border-slate-200" />
                </>
              )}
              <div className="text-[10px] font-bold uppercase tracking-widest text-steel mb-2 px-1">Accesos rápidos</div>
              {quickActions.map((x) => (
                <button
                  key={x.path}
                  type="button"
                  onClick={() => goAction(x.path)}
                  className="w-full text-left px-3 py-3 min-h-[48px] hover:bg-slate-50 flex items-center gap-3 border border-transparent hover:border-slate-200 transition-colors"
                >
                  <x.icon size={16} weight="bold" className="text-blueprint shrink-0" />
                  <span className="text-sm font-medium">{x.label}</span>
                  <span className="ml-auto text-steel text-xs">→</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 border-t-2 border-slate-200 flex items-center gap-4 text-[10px] text-steel bg-slate-50">
          <span><kbd className="font-mono border border-slate-300 px-1 rounded">↑↓</kbd> navegar</span>
          <span><kbd className="font-mono border border-slate-300 px-1 rounded">↵</kbd> abrir</span>
          <span><kbd className="font-mono border border-slate-300 px-1 rounded">Esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  );
}
